import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ArrowLeft, LogOut, Search, Loader2, CheckCircle2, Clock } from 'lucide-react';

export default function CheckOutFlow({ onCancel, onComplete }) {
  const [visitors, setVisitors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [search, setSearch] = useState('');
  const [departedVisitor, setDepartedVisitor] = useState(null);
  const [countdown, setCountdown] = useState(6);

  const fetchOnSite = async () => {
    try {
      const res = await axios.get('/api/visitors/on-site');
      setVisitors(res.data || []);
    } catch (e) {
      console.error('Failed to load on site visitors:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOnSite();
  }, []);

  // Countdown timer on success departure screen
  useEffect(() => {
    if (departedVisitor) {
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            onComplete();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [departedVisitor, onComplete]);

  const handleSignOut = async (v) => {
    setSubmitting(true);
    try {
      const res = await axios.post('/api/visitors/check-out', {
        session_id: v.session_id || v.id,
        registration: v.registration,
      });
      setDepartedVisitor({
        name: v.name,
        duration: res.data.duration || '—',
      });
    } catch (e) {
      console.error('Sign-out failed:', e);
      const msg = e?.response?.data?.detail || e?.response?.data?.message || 'Failed to sign out. Please see reception.';
      alert(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setSubmitting(false);
    }
  };

  const filtered = visitors.filter((v) => {
    const q = search.toLowerCase();
    return (
      (v.name || '').toLowerCase().includes(q) ||
      (v.registration || '').toLowerCase().includes(q) ||
      (v.company || '').toLowerCase().includes(q)
    );
  });

  return (
    <div className="w-full max-w-2xl mx-auto flex flex-col justify-center min-h-[70vh] p-4">
      {departedVisitor ? (
        /* Departure Success Screen */
        <div className="glass-panel-glow rounded-3xl p-10 text-center space-y-6 animate-in zoom-in-95 duration-300">
          <div className="w-24 h-24 rounded-full bg-amber-500/20 border-2 border-amber-500 mx-auto flex items-center justify-center text-amber-400 shadow-xl shadow-amber-500/20">
            <CheckCircle2 className="w-14 h-14" />
          </div>

          <div className="space-y-2">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white font-display">
              Goodbye, {departedVisitor.name}!
            </h2>
            <p className="text-lg text-slate-300 font-medium">
              Thank you for visiting JSD IT. Have a safe journey!
            </p>
          </div>

          <div className="p-4 rounded-2xl bg-slate-900/80 border border-white/10 max-w-sm mx-auto text-sm text-slate-400 flex items-center justify-center gap-2">
            <Clock className="w-4 h-4 text-cyan-400" />
            <span>Total Time on Site: <strong className="text-white">{departedVisitor.duration}</strong></span>
          </div>

          <div className="pt-4 flex justify-center">
            <button
              onClick={onComplete}
              className="px-8 py-3.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-white font-bold text-sm border border-white/10 touch-btn"
            >
              Done (Returning in {countdown}s)
            </button>
          </div>
        </div>
      ) : (
        /* Visitor List to Sign Out */
        <div className="glass-panel-glow rounded-3xl p-6 sm:p-8 space-y-6 animate-in fade-in zoom-in-95 duration-200">
          <div className="flex items-center justify-between border-b border-white/10 pb-4">
            <button
              onClick={onCancel}
              className="flex items-center gap-2 text-sm text-slate-400 hover:text-white font-medium touch-btn"
            >
              <ArrowLeft className="w-4 h-4" /> Back to Welcome
            </button>
            <span className="text-xs uppercase tracking-widest text-amber-400 font-bold">Sign Out</span>
          </div>

          <div className="space-y-1 text-center sm:text-left">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-white font-display">Sign Out / Leaving Site</h2>
            <p className="text-slate-400 text-sm">Tap your name or registration below to sign out.</p>
          </div>

          {/* Search bar */}
          <div className="relative">
            <Search className="w-5 h-5 absolute left-4 top-3.5 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by your name or registration..."
              className="w-full py-3 pl-11 pr-4 rounded-xl bg-slate-900 border border-white/20 text-white text-base focus:outline-none focus:border-amber-400 shadow-inner"
            />
          </div>

          {/* List of On-site visitors */}
          <div className="space-y-2.5 max-h-[50vh] overflow-y-auto pr-1">
            {loading ? (
              <div className="flex items-center justify-center py-12 text-slate-400 gap-2">
                <Loader2 className="w-6 h-6 animate-spin text-amber-400" />
                <span>Loading on-site visitors...</span>
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-12 bg-slate-900/60 rounded-2xl border border-white/5 space-y-2">
                <p className="text-slate-300 font-medium">No on-site visitors found.</p>
                <p className="text-xs text-slate-500">
                  {search ? 'Try checking your search spelling.' : 'All visitors have already been signed out.'}
                </p>
              </div>
            ) : (
              filtered.map((v) => (
                <div
                  key={v.id || v.session_id}
                  className="p-4 rounded-2xl bg-slate-900/90 border border-white/10 hover:border-amber-500/50 flex flex-col sm:flex-row sm:items-center justify-between gap-3 transition-all shadow-md"
                >
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-white text-lg">{v.name}</span>
                      {v.company && (
                        <span className="text-xs px-2 py-0.5 rounded bg-slate-800 text-slate-400">
                          {v.company}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-400 flex flex-wrap gap-x-4">
                      <span>Visiting: <strong className="text-cyan-400">{v.visiting_host}</strong></span>
                      {v.check_in_time && (
                        <span>In since: <strong className="text-slate-300 font-mono">
                          {new Date(v.check_in_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </strong></span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    {v.registration && (
                      <div className="uk-plate px-3 py-1 text-xs">
                        {v.registration}
                      </div>
                    )}
                    <button
                      type="button"
                      disabled={submitting}
                      onClick={() => handleSignOut(v)}
                      className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-slate-950 font-bold text-sm shadow-md flex items-center gap-1.5 touch-btn"
                    >
                      <LogOut className="w-4 h-4" />
                      Sign Out
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
